import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Alert,
  Tooltip,
  Container,
  Card,
  InputAdornment,
  OutlinedInput,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Divider
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import authService from "../../services/AuthService";
import bidsService from "../../services/BidsService";
import MarketHeader from "../../components/market/MarketHeader";
import CompactAuthorityToggle from "../../components/authority/CompactAuthorityToggle";
import BidsInfoDialog from "../../components/market/BidsInfoDialog";
import { getCardImageUrl } from "./cardImageUtils";
import LazyCardImage from "./LazyCardImage";

/**
 * MyBidsPage Component - Displays and manages all user bids
 * 
 * @returns {JSX.Element} The rendered component
 */
const MyBidsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // State for bids management
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingBidId, setEditingBidId] = useState(null);
  const [editValues, setEditValues] = useState({});
  
  // Card data state (same as Market component)
  const [cards, setCards] = useState([]);
  const [cardDetails, setCardDetails] = useState([]);
  const showBaseImages = true; // Use constant instead of state
  
  // Authority management state (for component compatibility only)
  const [, setAuthorityStatus] = useState(null);

  // Info dialog state
  const [showInfo, setShowInfo] = useState(false);
  
  // User settings state
  const [userSettings, setUserSettings] = useState({ currency: 'DEC', minimumBalance: 0 });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState(null);
  
  // Bid creation confirmation dialog state
  const [showBidConfirmDialog, setShowBidConfirmDialog] = useState(false);
  const [pendingBidData, setPendingBidData] = useState(null);
  const [dontShowBidInfoAgain, setDontShowBidInfoAgain] = useState(false);
  
  // Handle authority status changes
  const handleAuthorityChange = (status) => {
    setAuthorityStatus(status);
    // Authority not used in MyBidsPage but required for component compatibility
  };
  
  // Function to load card data with Market API edition mapping
  const loadCardData = async () => {
    try {
      // Load card details first
      const detailsResponse = await fetch('https://api2.splinterlands.com/cards/get_details');
      if (!detailsResponse.ok) {
        throw new Error(`Failed to fetch card details: ${detailsResponse.status}`);
      }
      
      const detailsData = await detailsResponse.json();
      
      if (!detailsData || !Array.isArray(detailsData)) {
        throw new Error('Invalid card details data received');
      }
      const marketResponse = await fetch('https://api.splinterlands.com/market/for_sale_grouped');
      if (!marketResponse.ok) {
        throw new Error(`Failed to fetch market data for editions: ${marketResponse.status}`);
      }
      
      const marketData = await marketResponse.json();
      
      if (!marketData || !Array.isArray(marketData)) {
        throw new Error('Invalid market data received');
      }
      // Create edition mapping from market data
      const editionMap = new Map();
      marketData.forEach(item => {
        if (item.card_detail_id && item.edition !== undefined) {
          editionMap.set(item.card_detail_id, item.edition);
        }
      });
      
      // Enhance card details with correct edition information from market data
      const enhancedCardDetails = detailsData.map(card => {
        const marketEdition = editionMap.get(card.id);
        
        return {
          ...card,
          edition: marketEdition !== undefined ? marketEdition : 0, // fallback to alpha if not found
          originalEdition: card.edition, // preserve original if it existed
          editionSource: marketEdition !== undefined ? 'market-api' : 'fallback'
        };
      });
      
      setCardDetails(enhancedCardDetails);
      
      // Store market data for other uses
      if (marketData && Array.isArray(marketData)) {
        setCards(marketData);
      }
    } catch (error) {
      console.error("Error loading card data:", error);
      throw error; // Re-throw to be handled by caller
    }
  };
  
  // Helper function to get card details by ID - using EXACT same logic as Market tab
  const getCardDetails = (cardDetailId) => {
    if (cardDetails.length === 0) {
      return {};
    }
    
    // Use SAME logic as Market tab: direct array search with .find()
    const result = cardDetails.find(detail => detail.id === cardDetailId) || {};
    
    if (!result || !result.name) {
      // Special debugging for specific cards
      if (cardDetailId === 742) {
        const card742 = cardDetails.find(c => c.name === "Gonach Excavator");
        if (card742) return card742;
      }
      
      return {};
    }
  
    return result;
  };
  
  // Helper function to get card object (for image URL)
  const getCardObject = (cardId, details) => {
    // Parse the SplTrader card ID to extract foil info
    const parsedCard = bidsService.parseCardId(cardId);
    
    // Map SplTrader foil types to Splinterlands foil numbers
    let foilValue = 0; // default regular
    let goldValue = false;
    
    switch (parsedCard.foil) {
      case 'regular':
        foilValue = 0;
        goldValue = false;
        break;
      case 'gold':
        foilValue = 1;
        goldValue = true;
        break;
      case 'gold_arcane':
        foilValue = 2; // Gold Arcana
        goldValue = true;
        break;
      case 'black':
        foilValue = 3; // Black foil (previously called blockfoil)
        goldValue = false;
        break;
      case 'black_arcane':
        foilValue = 4; // Black Arcana
        goldValue = false;
        break;
      default:
        foilValue = 0;
        goldValue = false;
    }
    
    // Create a card object for image generation
    const cardObject = {
      card_detail_id: parseInt(parsedCard.baseId),
      edition: details?.edition !== undefined ? details.edition : 0, // Use enhanced edition from card details
      gold: goldValue,
      foil: foilValue,
      level: 1, // Default level
      // Add additional properties needed for image generation
      uid: `${parsedCard.baseId}_${foilValue}`,
      market_id: null,
      // Add type for Arcana detection
      type: details?.type
    };
    
    return cardObject;
  };
  
  // Helper function to get market price for a card
  const getMarketPrice = (cardId, details) => {
    if (!cards || cards.length === 0) return null;
    
    // Parse the SplTrader card ID to extract card info
    const parsedCard = bidsService.parseCardId(cardId);
    
    // Find matching card in market data
    const marketCard = cards.find(card => {
      if (card.card_detail_id !== parseInt(parsedCard.baseId)) return false;
      
      // Check if foil and gold properties match
      const cardFoil = card.foil || 0;
      const cardGold = Boolean(card.gold);
      
      switch (parsedCard.foil) {
        case 'regular':
          return cardFoil === 0 && !cardGold;
        case 'gold':
          return cardFoil === 1 && cardGold;
        case 'gold_arcane':
          return cardFoil === 2 && cardGold;
        case 'black':
          return cardFoil === 3 && !cardGold;
        case 'black_arcane':
          return cardFoil === 4 && !cardGold;
        default:
          return false;
      }
    });
    
    const marketPrice = marketCard?.low_price_bcx ? parseFloat(marketCard.low_price_bcx) : null;
    
    return marketPrice;
  };

  // Helper function to get max level BCX for Black Foil and Gold Arcana cards
  const getMaxLevelBcx = (cardDetails, foilType) => {
    if (!cardDetails || !foilType) return null;
    
    // Only for Black Foil and Gold Arcana cards (they are always max level)
    if (foilType !== 'black' && foilType !== 'gold_arcane' && foilType !== 'black_arcane') {
      return null;
    }
    
    const rarity = cardDetails.rarity;
    
    // BCX needed for max level based on rarity
    const maxLevelBcx = {
      1: 38,  // Common
      2: 22,  // Rare  
      3: 10,  // Epic
      4: 4    // Legendary
    };
    
    return maxLevelBcx[rarity] || null;
  };

  // Check if card is auto-max level (Black Foil or Gold Arcana)
  const isAutoMaxLevel = (foilType) => {
    return foilType === 'black' || foilType === 'gold_arcane' || foilType === 'black_arcane';
  };

  // Get current user and initialize authentication on component mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First check if user is logged in
        if (!authService.isLoggedIn()) {
          setError("Please log in to view your bids");
          setLoading(false);
          return;
        }

        const user = authService.getCurrentUser();
        
        // Only proceed if we have a real authenticated user
        if (user && user.username) {
          setCurrentUser(user);
          
          // Proactively ensure we have a valid API token
          try {
            await authService.ensureValidApiToken();
          } catch (tokenError) {
            console.log("Pre-validating API token:", tokenError.message);
            // Don't set error here, let loadBids handle it
          }
          
          // Load card data only for authenticated users
          try {
            await loadCardData();
          } catch (error) {
            setError("Error loading card data");
            setLoading(false);
          }
        } else {
          // No authenticated user found
          setError("Please log in to view your bids");
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error("Error getting current user in MyBidsPage:", error);
        setError("Authentication error - please log in");
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Load user settings
  const loadUserSettings = useCallback(async () => {
    // Only load settings if we have a real authenticated user
    if (!currentUser?.username || !authService.isLoggedIn()) {
      console.log("No authenticated user - skipping user settings load");
      return;
    }
    
    setSettingsLoading(true);
    setSettingsError(null);
    
    try {
      const settings = await bidsService.getUserSettings();
      setUserSettings(settings);
    } catch (error) {
      console.error("Error loading user settings:", error);
      setSettingsError(error.message);
    } finally {
      setSettingsLoading(false);
    }
  }, [currentUser?.username]);

  // **PERFORMANCE OPTIMIZED**: Function to load bids from SplTrader API with smart loading
  const loadBids = useCallback(async () => {
    setLoading(true);
    setError(null); // Clear previous errors
    
    try {
      // Check if card details are loaded
      if (cardDetails.length === 0) {
        setError("Loading card details...");
        return;
      }
      
      // Enhanced authentication check with automatic token refresh
      if (!authService.isLoggedIn()) {
        setError("Please log in to view your bids");
        setLoading(false);
        return;
      }

      // Proactively ensure we have a valid API token before making requests
      try {
        await authService.ensureValidApiToken();
      } catch (authError) {
        console.log("Auth token validation/refresh in progress:", authError.message);
        
        // If it's a token refresh issue, try to handle it gracefully
        if (authError.message.includes('Token refresh needed') || 
            authError.message.includes('Authentication required')) {
          
          // Show a more user-friendly loading message
          setError("Refreshing authentication - please wait...");
          
          // Try to refresh the token silently
          try {
            await authService.refreshApiToken();
            setError(null); // Clear the refresh message
          } catch (refreshError) {
            setError("Authentication refresh failed - please log in again");
            setLoading(false);
            return;
          }
        } else {
          throw authError; // Re-throw if it's a different error
        }
      }
      
      // **PERFORMANCE**: Use optimized getBids with caching
      const splTraderBids = await bidsService.getBids();
      
      // Ensure we have an array
      const bidsArray = Array.isArray(splTraderBids) ? splTraderBids : [];
      
      // **PERFORMANCE**: Use more efficient map for conversion
      const convertedBids = bidsArray.map(bid => {
        const baseBid = bidsService.convertFromSplTraderFormat(bid);
        
        // Get card details for this bid
        const cardDetailId = bidsService.parseCardId(baseBid.id).baseId;
        const details = getCardDetails(parseInt(cardDetailId));
        
        const cardObject = getCardObject(baseBid.id, details);
        
        // Generate card image URL
        const cardImageUrl = getCardImageUrl(cardObject, details, showBaseImages);
        
        // Add card information to the bid
        return {
          ...baseBid,
          cardName: details?.name || `Unknown Card ${cardDetailId}`,
          cardDetails: details,
          cardObject: cardObject,
          cardImageUrl: cardImageUrl
        };
      });
      
      setBids(convertedBids);
      setLoading(false);
    } catch (error) {
      console.error("Error loading bids:", error);
      
      // Enhanced error handling for authenticated users
      let errorMessage = "Error loading bids";
      
      if (error.message.includes('Authentication required') || 
          error.message.includes('Authentifizierung erforderlich') ||
          error.message.includes('Token refresh needed')) {
        
        // Check if user is actually logged in but token might be expired
        if (authService.isLoggedIn()) {
          errorMessage = "Refreshing authentication - please wait...";
          
          // Automatically try to refresh the token and retry (only once)
          if (!loadBids._isRetrying) {
            loadBids._isRetrying = true;
            
            setTimeout(async () => {
              try {
                await authService.refreshApiToken();
                await loadBids(); // Retry loading bids
              } catch (refreshError) {
                setError("Authentication refresh failed - please log in again");
                setLoading(false);
              } finally {
                loadBids._isRetrying = false;
              }
            }, 1000);
            
            setError(errorMessage);
            return; // Don't set loading to false yet
          } else {
            errorMessage = "Authentication refresh failed - please log in again";
          }
        } else {
          errorMessage = "Please log in to view your bids";
        }
      } else if (error.message.includes('Invalid token')) {
        if (authService.isLoggedIn()) {
          errorMessage = "Token expired - automatically refreshing...";
          
          // Clear cache and retry (only if not already retrying)
          if (!loadBids._isRetrying) {
            loadBids._isRetrying = true;
            authService.clearApiTokenCache();
            
            setTimeout(async () => {
              try {
                await loadBids();
              } finally {
                loadBids._isRetrying = false;
              }
            }, 1500);
            
            setError(errorMessage);
            return;
          } else {
            errorMessage = "Token refresh failed - please log in again";
          }
        } else {
          errorMessage = "Authentication error - please log in";
        }
      } else if (error.message.includes('Keychain') && authService.isLoggedIn()) {
        errorMessage = "Connection to Hive Keychain failed - please try again";
      } else if (error.message.includes('6 Sekunden') || error.message.includes('timeout')) {
        errorMessage = "Server responding slowly - trying again in a few seconds";
      } else if (error.message.includes('CORS') || error.message.includes('Netzwerk')) {
        errorMessage = "Connection problem - check your internet connection";
      } else {
        errorMessage = "Error loading bids: " + error.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  }, [cardDetails.length, getCardDetails, showBaseImages]);

  // Load bids when card details become available and user is set
  useEffect(() => {
    if (cardDetails.length > 0 && currentUser) {
      // Add a small delay to ensure auth is fully initialized
      const timer = setTimeout(() => {
        loadBids();
        loadUserSettings();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [cardDetails.length, currentUser, loadBids, loadUserSettings]);
  
  // Add automatic retry for authenticated users
  const retryWithRefresh = async () => {
    if (!authService.isLoggedIn()) {
      setError("Please log in to view your bids");
      return;
    }
    
    try {
      setLoading(true);
      setError("Refreshing authentication - please wait...");
      
      // Clear any cached tokens and refresh
      authService.clearApiTokenCache();
      
      // Ensure we have a valid token
      await authService.ensureValidApiToken();
      
      // Clear the refresh message and reload bids
      setError(null);
      await loadBids();
    } catch (error) {
      setError("Authentication refresh failed - please log in again: " + error.message);
      setLoading(false);
    }
  };
  
  // Save user settings
  const saveUserSettings = async (newSettings) => {
    // Only save settings if we have a real authenticated user
    if (!currentUser?.username || !authService.isLoggedIn()) {
      console.log("No authenticated user - cannot save settings");
      setSettingsError("Please log in to save settings");
      return;
    }
    
    setSettingsLoading(true);
    setSettingsError(null);
    
    try {
      await bidsService.updateUserSettings(newSettings);
      setUserSettings(newSettings);
    } catch (error) {
      console.error("Error saving user settings:", error);
      setSettingsError(error.message);
    } finally {
      setSettingsLoading(false);
    }
  };
  
  // Filter bids when search term or status filter changes
  useEffect(() => {
    if (!bids.length) {
      setFilteredBids([]);
      return;
    }
    
    let filtered = [...bids];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(bid => 
        bid.cardName.toLowerCase().includes(term) ||
        bid.user.toLowerCase().includes(term)
      );
    }
    
    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(bid => {
        if (statusFilter === "active") return bid.active;
        if (statusFilter === "inactive") return !bid.active;
        return true;
      });
    }
    
    setFilteredBids(filtered);
  }, [bids, searchTerm, statusFilter]);
  
  // Handle bid deletion
  const handleDeleteBid = async (bidId) => {
    try {
      // Send delete request to SplTrader API
      await bidsService.deleteBid(bidId);
      
      // Reload bids to reflect the deletion
      await loadBids();
      
      setError(null);
    } catch (error) {
      setError("Error deleting bid: " + error.message);
    }
  };
  
  // Start inline editing for a bid
  const startInlineEdit = (bid) => {
    // Check if this is an auto-level card and ensure correct max BCX per card
    const parsedCard = bidsService.parseCardId(bid.id);
    const maxLevelBcx = getMaxLevelBcx(bid.cardDetails, parsedCard.foil);
    const isAutoLevel = isAutoMaxLevel(parsedCard.foil);
    
    let maxBcxCard = bid.maxBcxCard;
    if (isAutoLevel && maxLevelBcx) {
      maxBcxCard = maxLevelBcx; // Auto-correct for auto-level cards
    }
    
    setEditingBidId(bid.id);
    setEditValues({
      maxPriceBcx: bid.maxPriceBcx.toString(),
      totalBcx: bid.totalBcx.toString(),
      maxBcxCard: maxBcxCard.toString(),
      active: bid.active
    });
  };
  
  // Cancel inline editing
  const cancelInlineEdit = () => {
    setEditingBidId(null);
    setEditValues({});
  };
  
  // Save inline edits
  const saveInlineEdit = async (bidId) => {
    try {
      const bid = bids.find(b => b.id === bidId);
      if (!bid) {
        setError("Bid not found");
        return;
      }

      const maxPriceBcx = parseFloat(editValues.maxPriceBcx);
      const totalBcx = parseInt(editValues.totalBcx);
      let maxBcxCard = parseInt(editValues.maxBcxCard);

      // Validation: max BCX per card cannot be greater than total BCX
      if (maxBcxCard > totalBcx) {
        setError("Max BCX per card cannot be greater than total BCX");
        return;
      }

      // Handle auto-level cards (Black Foil, Gold Arcana)
      const parsedCard = bidsService.parseCardId(bidId);
      const maxLevelBcx = getMaxLevelBcx(bid.cardDetails, parsedCard.foil);
      const isAutoLevel = isAutoMaxLevel(parsedCard.foil);
      
      if (isAutoLevel && maxLevelBcx) {
        // Auto-correct max BCX per card for auto-level cards
        maxBcxCard = maxLevelBcx;
        
        // Warn user about unused BCX
        const remainingBcx = totalBcx % maxLevelBcx;
        if (remainingBcx > 0) {
          const cardsWillBuy = Math.floor(totalBcx / maxLevelBcx);
          const rarityNames = { 1: 'Common', 2: 'Rare', 3: 'Epic', 4: 'Legendary' };
          const rarityName = rarityNames[bid.cardDetails?.rarity] || 'Unknown';
          
          const warningMessage = `⚠️ Auto-level card detected!\n\n` +
            `This ${parsedCard.foil} ${rarityName} card requires ${maxLevelBcx} BCX per purchase.\n` +
            `With ${totalBcx} total BCX, you will buy ${cardsWillBuy} card(s), leaving ${remainingBcx} BCX unused.\n\n` +
            `Do you want to continue?`;
            
          if (!window.confirm(warningMessage)) {
            return; // User canceled
          }
        }
      }

      // Check if bid price is above market price (warning but not blocking)
      const marketPriceBcx = getMarketPrice(bidId, bid.cardDetails);
      if (marketPriceBcx && maxPriceBcx > marketPriceBcx) {
        const pricePercentage = ((maxPriceBcx / marketPriceBcx - 1) * 100).toFixed(1);
        const warningMessage = `⚠️ Warning: Your updated bid ($${maxPriceBcx.toFixed(3)}/BCX) is ${pricePercentage}% above the current market price ($${marketPriceBcx.toFixed(3)}/BCX). Do you want to save this bid?`;
        
        if (!window.confirm(warningMessage)) {
          return; // User canceled
        }
      }
      
      // Create updated bid data in SplTrader format
      const bidData = {
        id: bidId,
        max_price_bcx: maxPriceBcx,
        max_card_bcx: maxBcxCard,
        total_bcx: totalBcx,
        active: editValues.active,
        use_pm_bid_price: false
      };
      
      // Send to SplTrader API
      await bidsService.createOrUpdateBid(bidData);
      
      // Reload bids to show the updated data
      await loadBids();
      
      setEditingBidId(null);
      setEditValues({});
      setError(null);
    } catch (error) {
      console.error("Error updating bid:", error);
      setError("Error updating bid: " + error.message);
    }
  };
  
  // Handle input changes during inline editing
  const handleEditValueChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Toggle active status directly
  const toggleActiveStatus = async (bidId) => {
    try {
      const bid = bids.find(b => b.id === bidId);
      if (!bid) {
        setError("Bid not found");
        return;
      }

      // Create bid data with toggled active status
      const bidData = {
        id: bidId,
        max_price_bcx: bid.maxPriceBcx,
        max_card_bcx: bid.maxBcxCard,
        total_bcx: bid.totalBcx,
        active: !bid.active,
        use_pm_bid_price: bid.use_pm_bid_price || false
      };
      // Send to SplTrader API
      await bidsService.createOrUpdateBid(bidData);
      
      // Reload bids to show the updated data
      await loadBids();
      
      setError(null);
    } catch (error) {
      setError("Error changing bid status: " + error.message);
    }
  };

  // Proceed with bid creation
  const proceedWithBidCreation = async (bidData) => {
    try {
      // Save the "don't show again" preference
      if (dontShowBidInfoAgain) {
        localStorage.setItem('skipBidCreationInfo', 'true');
      }
      
      // Close dialog first
      setShowBidConfirmDialog(false);
      setPendingBidData(null);
      setDontShowBidInfoAgain(false);
      
      // Create the bid
      await bidsService.createOrUpdateBid(bidData);
      
      // Reload bids to show the new data
      await loadBids();
      
      setError(null);
    } catch (error) {
      console.error("Error creating bid:", error);
      setError("Error creating bid: " + error.message);
    }
  };

  // Cancel bid creation
  const cancelBidCreation = () => {
    setShowBidConfirmDialog(false);
    setPendingBidData(null);
    setDontShowBidInfoAgain(false);
  };

  
  return (
    <Container maxWidth="xl">
      {/* Market Header */}
      <MarketHeader 
        title="My Bids" 
        onInfoClick={() => setShowInfo(true)}
      />
      
      <Card sx={{ p: 3, bgcolor: colors.primary[400], mb: 4, borderRadius: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4" fontWeight="bold">
              My Bids
            </Typography>
            <Tooltip title="How bids work">
              <IconButton
                onClick={() => setShowInfo(true)}
                size="small"
                sx={{ 
                  color: colors.blueAccent[400],
                  '&:hover': {
                    bgcolor: `${colors.blueAccent[400]}20`
                  }
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {/* Authority Toggle integrated in header */}
            <CompactAuthorityToggle onAuthorityChange={handleAuthorityChange} />
            
            {/* Payment Currency Settings */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color={colors.grey[300]} sx={{ whiteSpace: 'nowrap' }}>
                Payment:
              </Typography>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 100 }}>
                <InputLabel id="currency-label" sx={{ color: colors.grey[300] }}>Currency</InputLabel>
                <Select
                  labelId="currency-label"
                  value={userSettings.currency}
                  onChange={(e) => {
                    const newSettings = { ...userSettings, currency: e.target.value };
                    setUserSettings(newSettings);
                    saveUserSettings(newSettings);
                  }}
                  label="Currency"
                  disabled={settingsLoading}
                  sx={{
                    bgcolor: colors.primary[500],
                    color: colors.grey[100],
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.grey[500],
                    }
                  }}
                >
                  <MenuItem value="DEC">DEC</MenuItem>
                  <MenuItem value="CREDITS">CREDITS</MenuItem>
                </Select>
              </FormControl>
              {settingsLoading && (
                <CircularProgress size={16} sx={{ color: colors.blueAccent[400] }} />
              )}
            </Box>
            
            <TextField
              variant="outlined"
              size="small"
              type="number"
              label={`Min Balance (${userSettings.currency})`}
              value={userSettings.minimumBalance}
              onChange={(e) => {
                const newBalance = Number(e.target.value);
                const newSettings = { ...userSettings, minimumBalance: newBalance };
                setUserSettings(newSettings);
              }}
              onBlur={() => saveUserSettings(userSettings)}
              disabled={settingsLoading}
              inputProps={{ min: 0, step: 1 }}
              sx={{
                minWidth: 140,
                '& .MuiOutlinedInput-root': {
                  bgcolor: colors.primary[500],
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.grey[500],
                  }
                },
                '& .MuiInputLabel-root': {
                  color: colors.grey[300],
                }
              }}
            />
          </Box>
        </Box>
        
        {/* Filters */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          mb={3}
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
        >
          <FormControl sx={{ width: { xs: '100%', sm: '300px' } }}>
            <OutlinedInput
              placeholder="Search cards..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.grey[500] }} />
                </InputAdornment>
              }
              sx={{
                bgcolor: colors.primary[500],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.grey[500],
                }
              }}
            />
          </FormControl>
          
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Box display="flex" alignItems="center">
              <FilterListIcon sx={{ mr: 1, }} />
              <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="status-filter-label" sx={{ color: colors.grey[300] }}>Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{
                    bgcolor: colors.primary[500],
                    color: colors.grey[100],
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: colors.grey[500],
                    }
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Button 
              variant="outlined"
              size="small"
              color='secondary'
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
        
        {/* Settings Error Display */}
        {settingsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Settings Error: {settingsError}
          </Alert>
        )}
      
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert 
            severity={error.includes("Please log in") ? "info" : "error"}
            sx={{ my: 2 }}
            action={
              !error.includes("Please log in") && (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={authService.isLoggedIn() ? retryWithRefresh : loadBids}
                  variant="outlined"
                  sx={{ 
                    ml: 2,
                    borderColor: 'currentColor',
                    '&:hover': {
                      borderColor: 'currentColor',
                      bgcolor: error.includes("Please log in") 
                        ? 'rgba(2, 136, 209, 0.1)' 
                        : 'rgba(211, 47, 47, 0.1)'
                    }
                  }}
                >
                  {authService.isLoggedIn() ? 'Refresh Auth' : 'Retry'}
                </Button>
              )
            }
          >
            {error.includes("Please log in") ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Authentication Required
                </Typography>
                <Typography variant="body1">
                  Please log in with your Hive account to view and manage your bids. 
                  Use the login button in the top right corner to get started.
                </Typography>
              </Box>
            ) : (
              error
            )}
          </Alert>
        ) : filteredBids.length === 0 ? (
          <Box p={4} textAlign="center" bgcolor={colors.primary[500]} borderRadius={1}>
            <Typography variant="h6" color={colors.grey[300]}>
              No bids found
            </Typography>
            <Typography variant="body1" color={colors.grey[400]} sx={{ mt: 1 }}>
              {bids.length > 0 
                ? "Try changing your search or filter criteria" 
                : "You haven't placed any bids yet. Create your first bid to start finding cards!"}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ bgcolor: colors.primary[500] }}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Card</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Price per BCX</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Total BCX</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Purchased</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Max BCX Card</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }}>Active</TableCell>
                  <TableCell sx={{ fontWeight: "bold", fontSize: '0.95rem' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBids.map((bid) => (
                  <TableRow key={bid.id} sx={{
                    opacity: bid.active ? 1 : 0.6,
                    bgcolor: bid.active ? 'inherit' : `${colors.grey[700]}40`
                  }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* Card Image */}
                        <Box
                          sx={{
                            width: 50,
                            height: 70,
                            borderRadius: 1,
                            border: `1px solid ${colors.primary[600]}`,
                            bgcolor: colors.primary[300],
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                        >
                          <LazyCardImage
                            card={bid.cardObject}
                            details={bid.cardDetails}
                            showBaseImages={showBaseImages}
                            alt={bid.cardName}
                            showArcanaEffect={bid.cardDetails?.type === "Arcana"}
                            sx={{
                              width: '100%',
                              height: '100%'
                            }}
                          />
                        </Box>
                        {/* Card Name */}
                        <Box>
                          <Typography variant="body1" fontWeight="bold" noWrap>
                            {bid.cardName}
                          </Typography>
                          <Typography variant="body2" color={colors.grey[400]} noWrap>
                            ID: {bid.id} ({bid.foilType || 'regular'})
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    {/* Price per BCX */}
                    <TableCell>
                      {editingBidId === bid.id ? (
                        <Box>
                          {(() => {
                            const marketPrice = getMarketPrice(bid.id, bid.cardDetails);
                            const bidPrice = parseFloat(editValues.maxPriceBcx);
                            const isPriceAboveMarket = marketPrice && !isNaN(bidPrice) && bidPrice > marketPrice;
                            
                            return (
                              <>
                                <TextField
                                  value={editValues.maxPriceBcx}
                                  onChange={(e) => handleEditValueChange('maxPriceBcx', e.target.value)}
                                  type="number"
                                  variant="standard"
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">USD</InputAdornment>,
                                    inputProps: { min: 0.001, step: 0.001, style: { width: '80px' } }
                                  }}
                                  size="small"
                                  sx={{
                                    '& .MuiInput-underline:before': {
                                      borderBottomColor: isPriceAboveMarket 
                                        ? colors.redAccent[400] : colors.blueAccent[400]
                                    },
                                    '& .MuiInput-underline:after': {
                                      borderBottomColor: isPriceAboveMarket 
                                        ? colors.redAccent[200] : colors.blueAccent[200]
                                    }
                                  }}
                                />
                                {/* Market price and warning */}
                                <Box sx={{ mt: 0.3 }}>
                                  {marketPrice && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        color: colors.grey[400],
                                        fontSize: '0.65rem'
                                      }}
                                    >
                                      Market: ${marketPrice.toFixed(3)}/BCX
                                    </Typography>
                                  )}
                                  {isPriceAboveMarket && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        color: colors.redAccent[400],
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      ⚠️ Above market!
                                    </Typography>
                                  )}
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      ) : (
                        `$${bid.maxPriceBcx} USD`
                      )}
                    </TableCell>
                    
                    {/* Total BCX */}
                    <TableCell>
                      {editingBidId === bid.id ? (
                        <Box>
                          {(() => {
                            const parsedCard = bidsService.parseCardId(bid.id);
                            const maxLevelBcx = getMaxLevelBcx(bid.cardDetails, parsedCard.foil);
                            const isAutoLevel = isAutoMaxLevel(parsedCard.foil);
                            const totalBcxValue = parseInt(editValues.totalBcx || 0);
                            const maxBcxCardValue = parseInt(editValues.maxBcxCard || 0);
                            
                            // Calculate how many cards will be bought and remaining BCX
                            let cardsWillBuy = 0;
                            let remainingBcx = totalBcxValue;
                            
                            if (isAutoLevel && maxLevelBcx) {
                              // For auto-level cards, each purchase uses the full max level BCX
                              cardsWillBuy = Math.floor(totalBcxValue / maxLevelBcx);
                              remainingBcx = totalBcxValue % maxLevelBcx;
                            } else if (maxBcxCardValue > 0) {
                              // For regular cards, use the max BCX per card setting
                              cardsWillBuy = Math.ceil(totalBcxValue / maxBcxCardValue);
                            }
                            
                            const hasValidationIssue = maxBcxCardValue > totalBcxValue;
                            const hasAutoLevelWarning = isAutoLevel && maxLevelBcx && remainingBcx > 0;
                            
                            return (
                              <>
                                <TextField
                                  value={editValues.totalBcx}
                                  onChange={(e) => handleEditValueChange('totalBcx', e.target.value)}
                                  type="number"
                                  variant="standard"
                                  InputProps={{ 
                                    inputProps: { min: 1, style: { width: '60px' } }
                                  }}
                                  size="small"
                                  sx={{
                                    '& .MuiInput-underline:before': {
                                      borderBottomColor: hasValidationIssue || hasAutoLevelWarning
                                        ? colors.redAccent[400] : colors.blueAccent[400]
                                    },
                                    '& .MuiInput-underline:after': {
                                      borderBottomColor: hasValidationIssue || hasAutoLevelWarning
                                        ? colors.redAccent[200] : colors.blueAccent[200]
                                    }
                                  }}
                                />
                                <Box sx={{ mt: 0.3 }}>
                                  {/* Total amount display */}
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      display: 'block',
                                      color: colors.blueAccent[300],
                                      fontSize: '0.65rem',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    ${(parseFloat(editValues.maxPriceBcx || 0) * totalBcxValue).toFixed(3)} total
                                  </Typography>
                                  
                                  {/* Auto-level card warning */}
                                  {isAutoLevel && maxLevelBcx && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        color: remainingBcx > 0 ? colors.redAccent[400] : colors.blueAccent[400],
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {cardsWillBuy} cards @ {maxLevelBcx} BCX
                                      {remainingBcx > 0 && ` (${remainingBcx} BCX unused)`}
                                    </Typography>
                                  )}
                                  
                                  {/* Validation warning */}
                                  {hasValidationIssue && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        color: colors.redAccent[400],
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      ⚠️ Max per card &gt; Total!
                                    </Typography>
                                  )}
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {bid.totalBcx}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: 'block',
                              color: colors.blueAccent[300],
                              fontSize: '0.65rem',
                              fontWeight: 'bold'
                            }}
                          >
                            ${(bid.maxPriceBcx * bid.totalBcx).toFixed(3)} total
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    
                    {/* Purchased BCX */}
                    <TableCell>
                      {(() => {
                        const originalBcx = bid.originalTotalBcx || bid.totalBcx;
                        const purchasedBcx = originalBcx - bid.totalBcx;
                        
                        return (
                          <Box>
                            <Typography variant="body2" sx={{ 
                              color: purchasedBcx > 0 ? colors.greenAccent[400] : colors.grey[300],
                              fontWeight: purchasedBcx > 0 ? 'bold' : 'normal'
                            }}>
                              {purchasedBcx}
                            </Typography>
                            {purchasedBcx > 0 && (
                              <Typography variant="caption" sx={{ 
                                color: colors.grey[400],
                                fontSize: '0.65rem'
                              }}>
                                of {originalBcx}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </TableCell>
                    
                    {/* Max BCX Card */}
                    <TableCell>
                      {editingBidId === bid.id ? (
                        <Box>
                          {(() => {
                            const parsedCard = bidsService.parseCardId(bid.id);
                            const maxLevelBcx = getMaxLevelBcx(bid.cardDetails, parsedCard.foil);
                            const isAutoLevel = isAutoMaxLevel(parsedCard.foil);
                            const maxBcxCardValue = parseInt(editValues.maxBcxCard || 0);
                            const totalBcxValue = parseInt(editValues.totalBcx || 0);
                            
                            const hasValidationIssue = maxBcxCardValue > totalBcxValue;
                            const shouldAutoUpdate = isAutoLevel && maxLevelBcx && maxBcxCardValue !== maxLevelBcx;
                            
                            return (
                              <>
                                <TextField
                                  value={editValues.maxBcxCard}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleEditValueChange('maxBcxCard', newValue);
                                    
                                    // Auto-update for auto-level cards
                                    if (isAutoLevel && maxLevelBcx && parseInt(newValue) !== maxLevelBcx) {
                                      // Show visual feedback that it will be auto-corrected
                                      setTimeout(() => {
                                        handleEditValueChange('maxBcxCard', maxLevelBcx.toString());
                                      }, 1000);
                                    }
                                  }}
                                  type="number"
                                  variant="standard"
                                  InputProps={{ 
                                    inputProps: { min: 1, style: { width: '60px' } }
                                  }}
                                  size="small"
                                  sx={{
                                    '& .MuiInput-underline:before': {
                                      borderBottomColor: hasValidationIssue || shouldAutoUpdate
                                        ? colors.redAccent[400] : colors.blueAccent[400]
                                    },
                                    '& .MuiInput-underline:after': {
                                      borderBottomColor: hasValidationIssue || shouldAutoUpdate
                                        ? colors.redAccent[200] : colors.blueAccent[200]
                                    }
                                  }}
                                />
                                <Box sx={{ mt: 0.3 }}>
                                  {/* Auto-level card info */}
                                  {isAutoLevel && maxLevelBcx && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        color: shouldAutoUpdate ? colors.redAccent[400] : colors.blueAccent[400],
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      {shouldAutoUpdate ? `Auto-corrects to ${maxLevelBcx}` : `Fixed at ${maxLevelBcx} BCX`}
                                    </Typography>
                                  )}
                                  
                                  {/* Validation warning */}
                                  {hasValidationIssue && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        display: 'block',
                                        color: colors.redAccent[400],
                                        fontSize: '0.65rem',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      ⚠️ Cannot exceed total
                                    </Typography>
                                  )}
                                </Box>
                              </>
                            );
                          })()}
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {bid.maxBcxCard}
                          </Typography>
                          {(() => {
                            const parsedCard = bidsService.parseCardId(bid.id);
                            const maxLevelBcx = getMaxLevelBcx(bid.cardDetails, parsedCard.foil);
                            const isAutoLevel = isAutoMaxLevel(parsedCard.foil);
                            
                            if (isAutoLevel && maxLevelBcx) {
                              return (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    display: 'block',
                                    color: colors.blueAccent[400],
                                    fontSize: '0.65rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  (Auto-level)
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                        </Box>
                      )}
                    </TableCell>
                    
                    {/* Active Status */}
                    <TableCell>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          if (editingBidId === bid.id) {
                            handleEditValueChange('active', !editValues.active);
                          } else {
                            toggleActiveStatus(bid.id);
                          }
                        }}
                        sx={{ 
                          color: editingBidId === bid.id 
                            ? (editValues.active ? colors.greenAccent[500] : colors.redAccent[400])
                            : (bid.active ? colors.greenAccent[500] : colors.redAccent[400]),
                          fontWeight: "bold",
                          p: 0
                        }}
                      >
                        {editingBidId === bid.id
                          ? (editValues.active ? "Yes" : "No")
                          : (bid.active ? "Yes" : "No")
                        }
                      </Button>
                    </TableCell>
                    
                    {/* Actions */}
                    <TableCell align="right">
                      {editingBidId === bid.id ? (
                        <>
                          <Tooltip title="Save changes">
                            <IconButton 
                              onClick={() => saveInlineEdit(bid.id)}
                              size="small"
                              sx={{ color: colors.greenAccent[400] }}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton 
                              onClick={cancelInlineEdit}
                              size="small"
                              sx={{ color: colors.redAccent[400] }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Edit bid">
                            <IconButton 
                              onClick={() => startInlineEdit(bid)}
                              size="small"
                              sx={{ color: colors.blueAccent[400] }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete bid">
                            <IconButton 
                              onClick={() => handleDeleteBid(bid.id)}
                              size="small"
                              sx={{ color: colors.redAccent[400] }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      
      {/* Bids Info Dialog */}
      <BidsInfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      
      {/* Bid Creation Confirmation Dialog */}
      <Dialog 
        open={showBidConfirmDialog} 
        onClose={cancelBidCreation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: colors.primary[400], color: colors.grey[100] }}>
          <Typography variant="h5" fontWeight="bold">
            Confirm Bid Creation
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: colors.primary[400], color: colors.grey[100], pt: 3 }}>
          {pendingBidData && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  How your bid works:
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Your bid will automatically purchase cards from the market when they are listed at or below your specified price
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • You'll buy up to <strong>{pendingBidData.totalBcx} BCX</strong> total, with a maximum of <strong>{pendingBidData.maxBcxCard} BCX per card</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • Your maximum total spend will be <strong>${(pendingBidData.maxPriceBcx * pendingBidData.totalBcx).toFixed(3)} USD</strong>
                </Typography>
                <Typography variant="body2">
                  • The bid will remain active until all BCX are purchased or you deactivate it
                </Typography>
              </Alert>
              
              <Divider sx={{ my: 2, borderColor: colors.grey[600] }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, color: colors.blueAccent[300] }}>
                  Bid Details:
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Card:</strong> {(() => {
                    const parsedCard = bidsService.parseCardId(pendingBidData.id);
                    const details = getCardDetails(parseInt(parsedCard.baseId));
                    return details?.name || `Card ${parsedCard.baseId}`;
                  })()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Foil Type:</strong> {(() => {
                    const parsedCard = bidsService.parseCardId(pendingBidData.id);
                    return parsedCard.foil || 'regular';
                  })()}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Price per BCX:</strong> ${pendingBidData.maxPriceBcx} USD
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Total BCX to buy:</strong> {pendingBidData.totalBcx}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Max BCX per card:</strong> {pendingBidData.maxBcxCard}
                </Typography>
                
                {(() => {
                  const parsedCard = bidsService.parseCardId(pendingBidData.id);
                  const details = getCardDetails(parseInt(parsedCard.baseId));
                  const maxBcx = getMaxLevelBcx(details, parsedCard.foil);
                  
                  if (maxBcx && isAutoMaxLevel(parsedCard.foil)) {
                    const rarityNames = { 1: 'Common', 2: 'Rare', 3: 'Epic', 4: 'Legendary' };
                    const rarityName = rarityNames[details?.rarity] || 'Unknown';
                    
                    return (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Special Card Type Detected!
                        </Typography>
                        <Typography variant="body2">
                          This is a <strong>{parsedCard.foil}</strong> {rarityName} card. These cards are always max level and require <strong>{maxBcx} BCX</strong> to trigger a purchase.
                        </Typography>
                      </Alert>
                    );
                  }
                  return null;
                })()}
              </Box>
            </>
          )}
          
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowBidInfoAgain}
                onChange={(e) => setDontShowBidInfoAgain(e.target.checked)}
                sx={{ color: colors.blueAccent[400] }}
              />
            }
            label="Don't show this dialog again"
            sx={{ color: colors.grey[300], mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: colors.primary[400], p: 3 }}>
          <Button 
            onClick={cancelBidCreation}
            variant="outlined"
            sx={{ 
              color: colors.grey[300],
              borderColor: colors.grey[500],
              '&:hover': {
                borderColor: colors.grey[400],
                bgcolor: `${colors.grey[500]}20`
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => proceedWithBidCreation(pendingBidData)}
            variant="contained"
            sx={{ 
              bgcolor: colors.blueAccent[500],
              color: colors.grey[100],
              '&:hover': {
                bgcolor: colors.blueAccent[400]
              }
            }}
          >
            Create Bid
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyBidsPage;
